import * as React from 'react';
import { useRef, useEffect, useState, useCallback } from 'react';
import { Box, Typography } from '@mui/material';
import { withTranslation } from 'react-i18next';
import {
  CALENDAR_NOTE_CONFIG,
  UNAVAILABILITY_CONFIG,
  UI_COLORS,
} from '../calendarConstants';
import MobileEventCard from './MobileEventCard';
import NonWorkingHoursOverlay from '../NonWorkingHoursOverlay';
import { hoursForDate } from '../../../utils/weekdayHours';

const HOUR_HEIGHT = 70;
const TIME_COLUMN_WIDTH = 34;
const MIN_COLUMN_WIDTH = 64;
const MAX_FIT_EMPLOYEES = 5;

function calculateEventOverlaps(dayEvents) {
  if (!dayEvents.length) return [];

  const eventsWithTimes = dayEvents.map((event) => {
    let startMinutes, endMinutes;
    if (event._clampedStartHour !== undefined && event._clampedEndHour !== undefined) {
      startMinutes = event._clampedStartHour * 60;
      endMinutes = event._clampedEndHour * 60;
    } else {
      const start = new Date(event.start_time);
      const end = new Date(event.end_time);
      startMinutes = start.getHours() * 60 + start.getMinutes();
      endMinutes = end.getHours() * 60 + end.getMinutes();
    }
    return { ...event, startMinutes, endMinutes };
  }).sort((a, b) => a.startMinutes - b.startMinutes);

  const columns = [];
  return eventsWithTimes.map((event) => {
    let columnIndex = columns.findIndex((colEnd) => event.startMinutes >= colEnd);
    if (columnIndex === -1) {
      columnIndex = columns.length;
      columns.push(event.endMinutes);
    } else {
      columns[columnIndex] = event.endMinutes;
    }
    return { ...event, overlapIndex: columnIndex, overlapTotal: columns.length };
  }).map((event, _, arr) => {
    const overlapping = arr.filter(
      (e) => e.startMinutes < event.endMinutes && e.endMinutes > event.startMinutes
    );
    const maxColumns = Math.max(...overlapping.map((e) => e.overlapIndex + 1));
    return { ...event, overlapTotal: maxColumns };
  });
}

function MobileMultiColumnView({
  t,
  selectedDate,
  events,
  unavailabilities,
  calendarNotes = [],
  workSchedule,
  // Grid rows (whole hours, the last one inclusive)
  startHour: START_HOUR = 8,
  endHour: END_HOUR = 16,
  onTimeSlotTap,
  onEventTap,
  resources,
  resourceType = 'employee',
  serviceBays = [],
}) {
  const gridScrollRef = useRef(null);
  const headerScrollRef = useRef(null);
  const containerRef = useRef(null);
  const [currentTimePosition, setCurrentTimePosition] = useState(null);
  const [containerWidth, setContainerWidth] = useState(0);

  // Measure container width for dynamic column sizing
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setContainerWidth(entry.contentRect.width);
      }
    });
    observer.observe(el);
    setContainerWidth(el.clientWidth);
    return () => observer.disconnect();
  }, []);

  // Scale columns to fit up to MAX_FIT_EMPLOYEES, scroll after that
  const availableWidth = containerWidth - TIME_COLUMN_WIDTH;
  const fittableCount = Math.min(resources.length, MAX_FIT_EMPLOYEES);
  const columnWidth = fittableCount > 0
    ? Math.max(MIN_COLUMN_WIDTH, Math.floor(availableWidth / fittableCount))
    : MIN_COLUMN_WIDTH;

  const hours = [];
  for (let h = START_HOUR; h <= END_HOUR; h++) {
    hours.push(h);
  }
  const totalHeight = (END_HOUR - START_HOUR) * HOUR_HEIGHT;

  // Scroll to the current time on mount, and when the grid's hours arrive with the calendar data
  useEffect(() => {
    if (gridScrollRef.current) {
      const now = new Date();
      const currentHour = now.getHours();
      if (currentHour >= START_HOUR && currentHour <= END_HOUR) {
        const scrollPosition = (currentHour - START_HOUR) * HOUR_HEIGHT - 50;
        gridScrollRef.current.scrollTop = Math.max(0, scrollPosition);
      }
    }
  }, [START_HOUR, END_HOUR]);

  // Current time line
  useEffect(() => {
    const updateCurrentTime = () => {
      const now = new Date();
      const isToday = now.toDateString() === selectedDate.toDateString();
      if (isToday) {
        const currentHour = now.getHours();
        const currentMinute = now.getMinutes();
        if (currentHour >= START_HOUR && currentHour < END_HOUR) {
          setCurrentTimePosition(
            (currentHour - START_HOUR) * HOUR_HEIGHT + (currentMinute / 60) * HOUR_HEIGHT
          );
        } else {
          setCurrentTimePosition(null);
        }
      } else {
        setCurrentTimePosition(null);
      }
    };
    updateCurrentTime();
    const interval = setInterval(updateCurrentTime, 60000);
    return () => clearInterval(interval);
  }, [selectedDate, START_HOUR, END_HOUR]);

  // Sync header scroll with grid scroll
  const handleGridScroll = useCallback(() => {
    if (gridScrollRef.current && headerScrollRef.current) {
      headerScrollRef.current.scrollLeft = gridScrollRef.current.scrollLeft;
    }
  }, []);

  const getEventStyle = (event) => {
    let startHour, endHour;
    if (event._clampedStartHour !== undefined && event._clampedEndHour !== undefined) {
      startHour = event._clampedStartHour;
      endHour = event._clampedEndHour;
    } else {
      const startTime = new Date(event.start_time);
      const endTime = new Date(event.end_time);
      startHour = startTime.getHours() + startTime.getMinutes() / 60;
      endHour = endTime.getHours() + endTime.getMinutes() / 60;
    }
    const top = Math.max(0, startHour - START_HOUR) * HOUR_HEIGHT;
    const height = (endHour - startHour) * HOUR_HEIGHT;
    // Colours live on MobileEventCard, which resolves the organization's state overrides and
    // the colour tag; here only geometry.
    return {
      top,
      height: Math.max(height, 30),
    };
  };

  // Where a span of start_time..end_time (an absence, a note) sits on the day's rows, clamped
  // to the shown hours
  const getSpanGeometry = (item) => {
    const startTime = new Date(item.start_time);
    const endTime = new Date(item.end_time);
    const dayStart = new Date(selectedDate);
    dayStart.setHours(START_HOUR, 0, 0, 0);
    const dayEnd = new Date(selectedDate);
    dayEnd.setHours(END_HOUR, 0, 0, 0);
    const effectiveStart = startTime < dayStart ? dayStart : startTime;
    const effectiveEnd = endTime > dayEnd ? dayEnd : endTime;
    const startHour = effectiveStart.getHours() + effectiveStart.getMinutes() / 60;
    const endHour = effectiveEnd.getHours() + effectiveEnd.getMinutes() / 60;
    const top = Math.max(0, startHour - START_HOUR) * HOUR_HEIGHT;
    const height = (endHour - startHour) * HOUR_HEIGHT;
    return { top, height: Math.max(height, 20) };
  };

  const getUnavailabilityStyle = (unavail) => {
    const config = UNAVAILABILITY_CONFIG[unavail.type] || UNAVAILABILITY_CONFIG.personal;
    return {
      ...getSpanGeometry(unavail),
      backgroundColor: config.bg,
      borderColor: config.color,
    };
  };

  // Does the span touch the selected day at all
  const touchesSelectedDay = (item) => {
    const startDate = new Date(item.start_time);
    const endDate = new Date(item.end_time);
    const dayStart = new Date(selectedDate);
    dayStart.setHours(0, 0, 0, 0);
    const dayEnd = new Date(selectedDate);
    dayEnd.setHours(23, 59, 59, 999);
    return startDate <= dayEnd && endDate >= dayStart;
  };

  // Filter unavailabilities for a specific resource on the selected day
  const getResourceUnavailabilities = (resourceId) => {
    if (resourceType !== 'employee') return [];
    return unavailabilities.filter((unavail) => touchesSelectedDay(unavail) && unavail.employee_id === resourceId);
  };

  // Notes pinned to one mechanic, in their column (bays have no notes; whole-workshop notes
  // belong to the combined view, as on the desktop)
  const getResourceNotes = (resourceId) => {
    if (resourceType !== 'employee') return [];
    return calendarNotes.filter((note) => touchesSelectedDay(note) && note.employee_id === resourceId);
  };

  // Filter events for a specific resource
  const getResourceEvents = (resourceId) => {
    if (resourceType === 'employee') {
      return events.filter((e) => e.employee_ids?.includes(resourceId));
    }
    return events.filter((e) => e.service_bay_id === resourceId);
  };

  if (!resources.length) {
    return (
      <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
        <Typography variant="body2" color="text.secondary" textAlign="center">
          {t('No employees yet')}
        </Typography>
      </Box>
    );
  }

  const columnsWidth = resources.length * columnWidth;

  return (
    <Box ref={containerRef} sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Employee Header Strip */}
      <Box
        sx={{
          flexShrink: 0,
          display: 'flex',
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
        }}
      >
        {/* Time column spacer */}
        <Box
          sx={{
            width: TIME_COLUMN_WIDTH,
            flexShrink: 0,
            borderRight: '1px solid',
            borderColor: 'divider',
            bgcolor: 'grey.50',
          }}
        />
        {/* Scrollable employee headers */}
        <Box
          ref={headerScrollRef}
          sx={{
            overflow: 'hidden',
            display: 'flex',
          }}
        >
          <Box sx={{ display: 'flex', width: columnsWidth }}>
            {resources.map((res) => (
              <Box
                key={res.id}
                sx={{
                  width: columnWidth,
                  flexShrink: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  py: 0.5,
                  borderRight: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: resourceType === 'employee' ? '50%' : '6px',
                    bgcolor: res.color || 'secondary.main',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontSize: '10px',
                    fontWeight: 600,
                  }}
                >
                  {res.initials || res.name?.charAt(0) || '?'}
                </Box>
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: '9px',
                    fontWeight: 500,
                    color: 'text.primary',
                    textAlign: 'center',
                    lineHeight: 1.1,
                    maxWidth: columnWidth - 4,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {res.name?.split(' ')[0] || res.name}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      {/* Scrollable Grid Area */}
      <Box
        ref={gridScrollRef}
        onScroll={handleGridScroll}
        sx={{
          flex: 1,
          overflow: 'auto',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            minHeight: totalHeight,
            position: 'relative',
          }}
        >
          {/* Sticky Time Column */}
          <Box
            sx={{
              width: TIME_COLUMN_WIDTH,
              flexShrink: 0,
              borderRight: '1px solid',
              borderColor: 'divider',
              bgcolor: 'grey.50',
              position: 'sticky',
              left: 0,
              zIndex: 3,
            }}
          >
            {hours.map((hour) => (
              <Box
                key={hour}
                sx={{
                  height: HOUR_HEIGHT,
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'flex-end',
                  pr: 0.5,
                  pt: 0.25,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Typography
                  variant="caption"
                  sx={{ color: 'text.secondary', fontWeight: 500, fontSize: '9px' }}
                >
                  {hour}:00
                </Typography>
              </Box>
            ))}
          </Box>

          {/* Resource Columns */}
          <Box sx={{ display: 'flex', width: columnsWidth }}>
            {resources.map((res) => {
              const resEvents = getResourceEvents(res.id);
              const resUnavails = getResourceUnavailabilities(res.id);
              const resNotes = getResourceNotes(res.id);
              const eventsWithOverlaps = calculateEventOverlaps(resEvents);

              return (
                <Box
                  key={res.id}
                  sx={{
                    width: columnWidth,
                    flexShrink: 0,
                    position: 'relative',
                    borderRight: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  {/* Hour grid lines */}
                  {hours.map((hour) => (
                    <Box
                      key={hour}
                      data-testid={`mobile-column-slot-${res.id}-${hour}`}
                      onClick={() => onTimeSlotTap(hour, res.id)}
                      sx={{
                        height: HOUR_HEIGHT,
                        borderBottom: '1px solid',
                        borderColor: UI_COLORS.hourLine,
                        position: 'relative',
                        cursor: 'pointer',
                        '&:active': { bgcolor: UI_COLORS.selectionBg },
                      }}
                    >
                      <Box
                        sx={{
                          position: 'absolute',
                          top: '50%',
                          left: 0,
                          right: 0,
                          height: '1px',
                          bgcolor: UI_COLORS.halfHourLine,
                        }}
                      />
                    </Box>
                  ))}

                  {/* Outside this column's work hours: the mechanic's own shift, or the
                      workshop's hours for a bay (the last row runs to END_HOUR + 1) */}
                  <NonWorkingHoursOverlay
                    hours={resourceType === 'employee'
                      ? (res.work_hours ? hoursForDate(res.work_hours, selectedDate) : undefined)
                      : hoursForDate(workSchedule?.days, selectedDate)}
                    startHour={START_HOUR}
                    endHour={END_HOUR + 1}
                    hourHeight={HOUR_HEIGHT}
                  />

                  {/* Unavailabilities */}
                  {resUnavails.map((unavail) => {
                    const style = getUnavailabilityStyle(unavail);
                    const config = UNAVAILABILITY_CONFIG[unavail.type] || UNAVAILABILITY_CONFIG.personal;
                    return (
                      <Box
                        key={unavail.id}
                        data-testid={`mobile-unavailability-${unavail.id}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          onEventTap({ ...unavail, _itemType: 'unavailability' });
                        }}
                        sx={{
                          position: 'absolute',
                          left: 2,
                          right: 2,
                          top: style.top,
                          height: style.height,
                          bgcolor: style.backgroundColor,
                          border: '1px dashed',
                          borderColor: style.borderColor,
                          borderRadius: 1,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          opacity: 0.8,
                          zIndex: 1,
                          cursor: 'pointer',
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            color: style.borderColor,
                            fontWeight: 500,
                            fontSize: '9px',
                            textAlign: 'center',
                          }}
                        >
                          {t(config.labelKey)}
                        </Typography>
                      </Box>
                    );
                  })}

                  {/* Notes pinned to this mechanic */}
                  {resNotes.map((note) => {
                    const style = getSpanGeometry(note);
                    return (
                      <Box
                        key={`note-${note.id}`}
                        data-testid={`mobile-calendar-note-${note.id}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          onEventTap({ ...note, _itemType: 'calendar_note' });
                        }}
                        sx={{
                          position: 'absolute',
                          left: 2,
                          right: 2,
                          top: style.top,
                          height: style.height,
                          bgcolor: CALENDAR_NOTE_CONFIG.bg,
                          borderLeft: '3px solid',
                          borderLeftColor: CALENDAR_NOTE_CONFIG.color,
                          borderRadius: 1,
                          display: 'flex',
                          alignItems: 'center',
                          px: 0.5,
                          overflow: 'hidden',
                          opacity: 0.9,
                          zIndex: 1,
                          cursor: 'pointer',
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            color: CALENDAR_NOTE_CONFIG.text,
                            fontWeight: 600,
                            fontSize: '9px',
                            lineHeight: 1.2,
                            overflow: 'hidden',
                            display: '-webkit-box',
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: 'vertical',
                          }}
                        >
                          {note.content}
                        </Typography>
                      </Box>
                    );
                  })}

                  {/* Events */}
                  {eventsWithOverlaps.map((event) => {
                    const style = getEventStyle(event);
                    const overlapLeft = event.overlapTotal > 1
                      ? `calc(2px + ${(event.overlapIndex / event.overlapTotal) * 100}%)`
                      : '2px';
                    const overlapWidth = event.overlapTotal > 1
                      ? `calc(${100 / event.overlapTotal}% - 4px)`
                      : 'calc(100% - 4px)';
                    return (
                      <MobileEventCard
                        key={event.id}
                        event={event}
                        style={{
                          ...style,
                          left: overlapLeft,
                          width: overlapWidth,
                        }}
                        onClick={() => onEventTap(event)}
                        isAllView={false}
                        employees={[]}
                        serviceBays={serviceBays}
                        showServiceBay={resourceType !== 'bay'}
                        narrow
                      />
                    );
                  })}
                </Box>
              );
            })}
          </Box>

          {/* Current time line spanning all columns */}
          {currentTimePosition !== null && (
            <Box
              sx={{
                position: 'absolute',
                left: TIME_COLUMN_WIDTH,
                right: 0,
                top: currentTimePosition,
                height: '2px',
                bgcolor: UI_COLORS.currentTimeLine,
                zIndex: 10,
                pointerEvents: 'none',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  left: -4,
                  top: -4,
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  bgcolor: UI_COLORS.currentTimeLine,
                },
              }}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
}

export default withTranslation()(MobileMultiColumnView);
